import {Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  Conditions,
  Offers,
  OfferSteps,
  Personen,
  Staff,
  WebserviceResponse
} from "../../../_shared/interfaces";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  ClrDatagridModule,
  ClrDatepickerModule,
  ClrInputModule,
  ClrRadioModule,
  ClrSelectModule,
  ClrStepperModule
} from "@clr/angular";
import { DatePipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import {
  AlertService,
  ConditionStoreService,
  IAlertType, OffersStoreService,
  PersonsStoreService,
  StaffStoreService
} from "../../../_shared/services";
import { Subject } from "rxjs";

import { environment as env} from "../../../../environments/environment";
import {TranslocoDirective} from "@jsverse/transloco";

import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';
import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-offer-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    ClrStepperModule,
    ReactiveFormsModule,
    ClrInputModule,
    ClrSelectModule,
    ClrDatepickerModule,
    NgIf,
    RouterLink,
    ClrRadioModule,
    NgForOf,
    ClrDatagridModule,
    DatePipe,
    JsonPipe,
    TranslocoDirective
  ],
  templateUrl: './offer-form.component.html',
  styleUrl: './offer-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})
export class OfferFormComponent implements OnInit, OnChanges {
  offerForm!: FormGroup;
  private defaultDate: Date = new Date();
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.offerForm);
  }
  companyId: number = 0;
  delivery_conditions: Conditions[] = [];
  payment_conditions: Conditions[] = [];
  offer_conditions: Conditions[] = [];

  @Input() projectId: string = '';
  @Input() offerId: number = 0;
  @Input() offersData?: Offers;
  @Input() isUpdateMode = true;
  @Output() formSubmit = new EventEmitter<OfferSteps>();

  devMode: boolean = false;
  crmMode: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private personsService: PersonsStoreService,
    private staffService: StaffStoreService,
    private condss: ConditionStoreService,
    private offss: OffersStoreService,
    private alertDispatcher: AlertService,
    private router: Router,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.crmMode = env.externalCrm;
    this.initForm();
  }

  ngOnInit() {
    this.loadStaff();
    this.loadPayConds();
    this.loadDeliveryConds();
    this.loadOfferConds();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['offersData'] && this.offersData) {
      this.patchFormValues(this.offersData);
    }
    this.companyId = parseInt(this.splitProjectNumber(this.projectId, 0));
    this.loadPersons(this.companyId);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.offerForm = this.fb.group({
      stepI: this.fb.group({
        project_id: [''],
        offer_id: [''],
        company_name: [''],
        street: [''],
        postcode: [''],
        city: [''],
        country: [''],
        status: [''],
        name_project: new FormControl('', [Validators.required, Validators.maxLength(100)]),
        description: [''],
        request_id: [''],
        order_id: [''],
        vat_id: [''],
        offer_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        budget: 0
      }),
      stepII: this.fb.group({
        employee_phm: this.fb.control<number | null>(null),
      }),
      stepIII: this.fb.group({
        employee_leader: this.fb.control<number | null>(null),
      }),
      stepIV: this.fb.group({
        cond_offer: new FormControl(null, [Validators.required, Validators.min(1)]),
        cond_payment: new FormControl(null, [Validators.required, Validators.min(1)]),
        cond_delivery: new FormControl(null, [Validators.required, Validators.min(1)]),
        delivery_remarks: [''],
      })
    })
  }

  private patchFormValues(data: Offers) {
    if (!data) return;

    const formValue = {
      stepI: {
        project_id: data.project_id || '',
        offer_id: data.offer_id || '',
        company_name: data.company_name || '',
        street: data.street || '',
        postcode: data.postcode || '',
        city: data.city || '',
        country: this.mapCountryForForm(data.country || ''),
        status: data.status || '',
        name_project:  data.name_project || '',
        description: data.description || '',
        request_id: data.request_id || '',
        order_id: data.order_id || '',
        vat_id: data.vat_id || '',
        offer_date: this.datePipe.transform(data.offer_date, 'dd.MM.yyyy'),
        budget:  data.budget || 0,
      },
      stepII: {
        employee_phm: data.employee_phm || 0,
      },
      stepIII: {
        employee_leader: data.employee_leader || 0,
      },
      stepIV: {
        cond_offer: parseInt(String(data.cond_offer)),
        cond_payment: parseInt(String(data.cond_payment)),
        cond_delivery: parseInt(String(data.cond_delivery)),
        delivery_remarks: data.delivery_remarks,
      }
    };
    this.offerForm.patchValue(formValue);
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  projectLeader(): number {
    return this.offerForm.get('stepIII')?.get('employee_leader')?.value || 0;
  }

  projectStaff(): number {
    return this.offerForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  get offerStatus(): string | null {
    return this.offerForm.get('stepI.status')?.value ?? null;
  }

  updateOffer() {
    if (this.offerForm.valid) {
      const formValue = this.offerForm.value;
      this.offss.updateOffer(formValue).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Offer erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(formValue);
          this.router.navigate(['/offer/details', this.projectId, this.offerId]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren der Offer: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }

  deleteOffer() {
    if (confirm('Initialisierung der Offer abbrechen?\nDer Eintrag wird gelöscht!')) {
      this.offss.deleteOffer(this.projectId, this.offerId)
        .subscribe({
          next: () => { },
          complete: () => {
            this.alertDispatcher.alertConfig.message += 'Die Offer ' + this.projectId + '.0003.' + this.offerId + ' wurde gelöscht!';
            this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
            this.router.navigate(['/projects/overview'])
          }
        });
    }
  }

  private loadStaff() {
    // getStaffById(0) traf auf /staff/get_single_staff/0 -- einen Mitarbeiter
    // mit der ID 0 gibt es nicht, die Auswahl blieb deshalb leer. Alle anderen
    // Formulare rufen getStaff().
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  private loadPersons(companyId: number){
    this.personsService.getPersonInCompany(companyId, true).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        // Sortiere die Liste nach dem Nachnamen
        this.persons.sort((a, b) => {
          if (a.lastname < b.lastname) {
            return -1;
          }
          if (a.lastname > b.lastname) {
            return 1;
          }
          return 0;
        });

      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Kunden: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadOfferConds() {
    this.condss.getConditionByType(3).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.offer_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadPayConds(){
    this.condss.getConditionByType(2).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.payment_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadDeliveryConds(){
    this.condss.getConditionByType(1).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.delivery_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  get isOfferValid(): boolean {
    return !!(this.offersData &&
      parseInt(String(this.offersData.cond_offer)) > 0 &&
        parseInt(String(this.offersData.cond_payment)) > 0 &&
          parseInt(String(this.offersData.cond_delivery)) > 0);
  }
}
